import React from "react";
import { Link } from "react-router-dom";
import { Sprout, Leaf, Recycle, Droplets, Sparkles, Cpu, Globe2, ShieldCheck, FileText, Briefcase, Rocket, ArrowRight } from "lucide-react";
import { FEATURES, WHY_CHOOSE, MVV, CERTIFICATIONS } from "../data/content";
import { FadeUp, Stagger, StaggerItem } from "./Motion";

const iconMap = { Sprout, Leaf, Recycle, Droplets, Sparkles, Cpu, Globe2, ShieldCheck, FileText, Briefcase, Rocket };

export const FeatureStrip = () => (
  <section data-testid="feature-strip" className="py-20 md:py-24 bg-cream border-b border-border-soft">
    <div className="container mx-auto px-6 md:px-10 xl:px-16">
      <Stagger className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-8">
        {FEATURES.map((f, i) => {
          const Icon = iconMap[f.icon] || Leaf;
          return (
            <StaggerItem key={f.title}>
              <div className="flex items-start gap-5" data-testid={`feature-${i}`}>
                <div className="shrink-0 w-14 h-14 rounded-full bg-cream-2 flex items-center justify-center text-forest">
                  <Icon size={22} strokeWidth={1.6} />
                </div>
                <div>
                  <p className="eyebrow text-saffron">0{i + 1}</p>
                  <h3 className="mt-2 font-serif text-2xl text-forest leading-tight">{f.title}</h3>
                  <p className="mt-3 text-muted-ink">{f.text}</p>
                </div>
              </div>
            </StaggerItem>
          );
        })}
      </Stagger>
    </div>
  </section>
);

export const AboutIntro = () => (
  <section data-testid="about-intro" className="py-24 md:py-32 bg-cream">
    <div className="container mx-auto px-6 md:px-10 xl:px-16">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
        <FadeUp className="lg:col-span-6 relative">
          <div className="relative aspect-[4/5] overflow-hidden">
            <img
              src="https://images.pexels.com/photos/10269715/pexels-photo-10269715.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1100&w=900"
              alt="Sustainable agriculture"
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
          <div className="absolute -bottom-8 -right-6 md:-right-10 w-40 h-40 md:w-56 md:h-56 bg-saffron text-cream p-6 flex flex-col justify-between">
            <span className="font-serif text-5xl md:text-6xl leading-none">R.</span>
            <div>
              <p className="eyebrow text-cream/80">Est.</p>
              <p className="font-serif text-2xl">2026 · Kolkata</p>
            </div>
          </div>
        </FadeUp>

        <FadeUp delay={0.15} className="lg:col-span-6">
          <p className="eyebrow text-forest">Agriculture & Organic Product Farm</p>
          <h2 className="mt-5 font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-forest">
            Innovating sustainable agriculture with <em className="italic text-saffron">quality</em>, integrity, and empowerment at its core.
          </h2>
          <p className="mt-8 text-muted-ink text-lg leading-relaxed">
            Rudrash Private Limited is redefining farming in eastern India with innovative, sustainable solutions. Specializing in controlled-environment and aeroponic cultivation, we offer high-quality products across Mushroom, Saffron, and Poultry lines — addressing modern agricultural challenges with eco-friendly, water-efficient practices.
          </p>

          <div className="mt-12 grid grid-cols-3 gap-3 md:gap-6">
            {[
              { t: "Mushroom", i: Leaf },
              { t: "Saffron", i: Sparkles },
              { t: "Poultry", i: Globe2 },
            ].map((x, idx) => (
              <div
                key={x.t}
                data-testid={`about-icon-${idx}`}
                className="border border-border-soft bg-cream-2/50 p-3 sm:p-5 flex flex-col items-start gap-2 sm:gap-3 hover:border-forest hover:bg-cream transition-colors overflow-hidden"
              >
                <x.i className="text-saffron w-5 h-5 sm:w-[22px] sm:h-[22px]" />
                <span className="font-serif text-base sm:text-xl text-forest break-words w-full">{x.t}</span>
              </div>
            ))}
          </div>
        </FadeUp>
      </div>
    </div>
  </section>
);

export const WhyChoose = () => (
  <section data-testid="why-choose" className="py-24 md:py-32 bg-cream-2 border-t border-border-soft">
    <div className="container mx-auto px-6 md:px-10 xl:px-16">
      <FadeUp className="max-w-2xl mb-24">
        <p className="tracking-[0.2em] font-semibold text-saffron uppercase text-xs">Why Choose Rudrash</p>
        <h2 className="mt-5 font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-forest">
          Built on four pillars that make modern farming actually sustainable.
        </h2>
      </FadeUp>

      <Stagger className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
        {WHY_CHOOSE.map((w, i) => {
          const Icon = iconMap[w.icon] || Leaf;
          return (
            <StaggerItem key={w.title}>
              <div
                data-testid={`why-choose-${i}`}
                className="p-8 md:p-12 border border-border-soft bg-cream min-h-[280px] flex flex-col justify-between transition-all duration-300 hover:-translate-y-2 hover:shadow-[0_12px_40px_rgba(0,0,0,0.06)] group"
              >
                <Icon size={32} strokeWidth={1.4} className="text-saffron" />
                <div className="mt-12">
                  <p className="eyebrow text-muted-ink">0{i + 1}</p>
                  <h3 className="mt-3 font-serif text-2xl md:text-3xl text-forest">{w.title}</h3>
                  <p className="mt-3 text-muted-ink max-w-md">{w.text}</p>
                </div>
              </div>
            </StaggerItem>
          );
        })}
      </Stagger>
    </div>
  </section>
);

export const MissionVisionValues = () => (
  <section data-testid="mvv" className="py-24 md:py-32 bg-forest text-cream relative overflow-hidden">
    <div className="grain" />
    <div className="container mx-auto px-6 md:px-10 xl:px-16 relative">
      <FadeUp className="max-w-3xl mb-20">
        <p className="eyebrow text-saffron">Our Compass</p>
        <h2 className="mt-4 font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
          Mission, Vision & Core Values.
        </h2>
      </FadeUp>

      <Stagger className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-12">
        {MVV.map((m, i) => (
          <StaggerItem key={m.label}>
            <div
              data-testid={`mvv-${i}`}
              className="relative pt-10 border-t border-cream/20 min-h-[320px] flex flex-col"
            >
              <span className="absolute -top-3.5 left-0 bg-forest pr-4 eyebrow text-saffron">
                0{i + 1} — {m.label}
              </span>
              <h3 className="font-serif text-3xl md:text-4xl leading-tight">{m.title}</h3>
              <p className="mt-6 text-cream/70 leading-relaxed">{m.body}</p>
            </div>
          </StaggerItem>
        ))}
      </Stagger>
    </div>
  </section>
);

export const CertificationsPreview = () => (
  <section data-testid="certifications-preview" className="py-24 md:py-32 bg-cream border-t border-border-soft">
    <div className="container mx-auto px-6 md:px-10 xl:px-16">
      <FadeUp className="max-w-2xl mb-20">
        <p className="text-xs font-semibold tracking-[0.2em] text-saffron uppercase mb-5">Certifications & Compliance</p>
        <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-forest">
          Built on verified standards and trusted registrations.
        </h2>
      </FadeUp>

      <Stagger className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
        {CERTIFICATIONS.map((cert) => {
          const Icon = iconMap[cert.icon] || FileText;
          return (
            <StaggerItem key={cert.id}>
              <div className="p-8 md:p-10 border border-border-soft bg-cream-2/30 h-full flex flex-col hover:bg-cream-2 transition-colors duration-300">
                <div className="flex flex-col gap-5 h-full">
                  <div className="w-14 h-14 rounded-full bg-cream flex items-center justify-center shrink-0 text-saffron border border-border-soft">
                    <Icon size={24} strokeWidth={1.5} />
                  </div>
                  <div className="flex-grow">
                    <h3 className="font-serif text-2xl md:text-3xl text-forest">{cert.title}</h3>
                    <p className="mt-3 text-muted-ink leading-relaxed">{cert.shortDesc}</p>
                  </div>
                  <div className="mt-6 pt-6 border-t border-border-soft">
                    <Link to="/certifications" className="text-saffron font-medium flex items-center gap-2 hover:text-forest transition-colors w-fit">
                      View Certificate <ArrowRight size={14} />
                    </Link>
                  </div>
                </div>
              </div>
            </StaggerItem>
          );
        })}
      </Stagger>

      <FadeUp className="mt-16 text-center">
        <Link to="/certifications" className="btn-primary inline-flex">
          View All Certifications
        </Link>
      </FadeUp>
    </div>
  </section>
);
